import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Activity, Check, Sparkles, Volume2, Mic, Smartphone } from "lucide-react";
import { PhoneticSegment } from "../types";

interface SyllableKaraokeAlignerProps {
  segments?: PhoneticSegment[];
  currentPitchHz?: number | null;
  targetPitchHz?: number;
  toleranceCents?: number;
  onSyllableComplete?: (index: number) => void;
  language?: string;
}

export const SyllableKaraokeAligner: React.FC<SyllableKaraokeAlignerProps> = ({
  segments = [
    { syllable: "ॐ", expectedPitch: "Pranava", phoneticType: "Udatta", description: "Sacred Om Vibrational Resonance" },
    { syllable: "भूः", expectedPitch: "Udatta", phoneticType: "High Accent", description: "Physical Realm Alignment" },
    { syllable: "भुवः", expectedPitch: "Svarita", phoneticType: "Mid Accent", description: "Astral Sphere Transition" },
    { syllable: "स्वः", expectedPitch: "Anudatta", phoneticType: "Low Accent", description: "Celestial Harmony" },
  ],
  currentPitchHz = 220,
  targetPitchHz = 220,
  toleranceCents = 35,
  onSyllableComplete,
  language = "en",
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [centDeviation, setCentDeviation] = useState(0);
  const [isMatched, setIsMatched] = useState(false);
  const [hapticCount, setHapticCount] = useState(0);
  const lastHapticRef = useRef<number>(0);

  // Compute cent deviation whenever current pitch changes
  useEffect(() => {
    if (!currentPitchHz || currentPitchHz <= 0 || !targetPitchHz) {
      setCentDeviation(0);
      setIsMatched(false);
      return;
    }

    const cents = 1200 * Math.log2(currentPitchHz / targetPitchHz);
    const absCents = Math.abs(cents);
    setCentDeviation(Math.round(cents));

    const matched = absCents <= toleranceCents;
    setIsMatched(matched);

    if (matched) {
      const now = Date.now();
      if (now - lastHapticRef.current > 300) {
        lastHapticRef.current = now;
        if (typeof window !== "undefined" && "vibrate" in navigator) {
          try {
            navigator.vibrate([25, 40, 25]);
            setHapticCount((prev) => prev + 1);
          } catch (e) {
            // Haptic trigger fallback
          }
        }
      }
    }
  }, [currentPitchHz, targetPitchHz, toleranceCents]);

  const activeSegment = segments[activeIndex] || segments[0];

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl space-y-6">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-4">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-400">
            <Sparkles className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <h3 className="font-bold text-slate-100 flex items-center gap-2">
              Vedic Phonetic Syllable Karaoke
              <span className="text-[10px] bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 px-2 py-0.5 rounded-full">
                ±{toleranceCents} Cents Range
              </span>
            </h3>
            <p className="text-xs text-slate-400">
              Real-time swara alignment matching Udatta, Svarita & Anudatta pitch contours
            </p>
          </div>
        </div>

        {/* Live Pitch Indicator */}
        <div className="text-right">
          <div className="text-xs font-mono font-bold text-slate-300">
            Live: <span className="text-amber-300">{currentPitchHz ? `${Math.round(currentPitchHz)} Hz` : "Silence"}</span>
          </div>
          <div className="text-[11px] font-mono text-slate-400">
            Target: <span className="text-emerald-400">{targetPitchHz} Hz</span>
          </div>
        </div>
      </div>

      {/* Main Visualizer Stage with Dynamic Ring */}
      <div className="relative flex flex-col items-center justify-center p-8 bg-slate-950/80 rounded-2xl border border-slate-800/80 overflow-hidden min-h-[200px]">
        {/* Dynamic Pitch Matching Rings */}
        <AnimatePresence>
          {isMatched && (
            <>
              {/* Outer Pulsing Green Ring */}
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: [1, 1.25, 1.1], opacity: [0.6, 0.9, 0.7] }}
                exit={{ scale: 0.8, opacity: 0 }}
                transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 m-auto w-48 h-48 rounded-full border-4 border-emerald-400/60 shadow-[0_0_50px_rgba(16,185,129,0.5)] bg-emerald-500/10 pointer-events-none"
              />
              {/* Inner Resonant Ring */}
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ duration: 0.8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 m-auto w-36 h-36 rounded-full border-2 border-teal-300/80 shadow-[0_0_30px_rgba(45,212,191,0.6)] pointer-events-none"
              />
            </>
          )}
        </AnimatePresence>

        {/* Syllable Display */}
        <motion.div
          key={activeIndex}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="relative z-10 text-center space-y-2"
        >
          <div className={`text-5xl font-extrabold tracking-wide transition-all ${
            isMatched ? "text-emerald-300 drop-shadow-[0_0_20px_rgba(52,211,153,0.8)]" : "text-amber-100"
          }`}>
            {activeSegment.syllable}
          </div>
          <div className="text-xs font-semibold text-amber-400 uppercase tracking-widest bg-amber-500/10 px-3 py-1 rounded-full border border-amber-500/30 inline-block">
            {activeSegment.expectedPitch} • {activeSegment.phoneticType}
          </div>
          <p className="text-xs text-slate-400 max-w-sm mx-auto">
            {activeSegment.description}
          </p>
        </motion.div>

        {/* Cent Deviation Meter Bar */}
        <div className="w-full max-w-md mt-6 space-y-1">
          <div className="flex justify-between text-[10px] font-mono font-bold text-slate-400">
            <span>-100 Cents (Flat)</span>
            <span className={isMatched ? "text-emerald-400 font-extrabold" : "text-slate-300"}>
              {centDeviation > 0 ? `+${centDeviation}` : centDeviation} Cents
            </span>
            <span>+100 Cents (Sharp)</span>
          </div>
          <div className="relative w-full bg-slate-800 h-2.5 rounded-full overflow-hidden border border-slate-700">
            {/* Target Alignment Center Marker */}
            <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-emerald-400 z-10 -translate-x-1/2 shadow-[0_0_8px_#34d399]" />
            {/* Live Indicator Bar */}
            <motion.div
              animate={{
                left: `${Math.max(0, Math.min(100, 50 + (centDeviation / 2)))}%`,
              }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`absolute top-0 bottom-0 w-3 -translate-x-1/2 rounded-full ${
                isMatched ? "bg-emerald-400 shadow-[0_0_12px_#34d399]" : "bg-amber-400 shadow-[0_0_8px_#fbbf24]"
              }`}
            />
          </div>
        </div>
      </div>

      {/* Syllable Selection Strip */}
      <div className="flex items-center justify-between gap-2 overflow-x-auto pb-2 pt-1">
        {segments.map((seg, idx) => (
          <button
            key={idx}
            onClick={() => {
              setActiveIndex(idx);
              if (onSyllableComplete) onSyllableComplete(idx);
            }}
            className={`flex-1 min-w-[90px] p-3 rounded-xl border text-center transition-all ${
              idx === activeIndex
                ? "bg-amber-500/20 border-amber-500/60 text-amber-200 shadow-lg font-bold"
                : "bg-slate-950/40 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"
            }`}
          >
            <div className="text-lg font-extrabold">{seg.syllable}</div>
            <div className="text-[10px] uppercase font-mono text-slate-400">{seg.expectedPitch}</div>
          </button>
        ))}
      </div>

      {/* Mobile Haptic Status */}
      <div className="flex items-center justify-between text-xs text-slate-400 pt-1 border-t border-slate-800/80">
        <div className="flex items-center space-x-2">
          <Smartphone className="w-3.5 h-3.5 text-amber-400" />
          <span>Haptic Vibration Engine: <span className="text-emerald-400 font-bold">Active</span></span>
        </div>
        <div className="font-mono text-[11px] text-amber-300">
          Matched Pulses: {hapticCount}
        </div>
      </div>
    </div>
  );
};

export default SyllableKaraokeAligner;
